// ============================================================================
// BrightPath - ToastNotification Component
// BP-12-03: Notifications Frontend
// ============================================================================

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink } from 'lucide-react';
import {
  Calendar,
  Heart,
  FileText,
  Users,
  Scale,
  ShoppingCart,
  Settings,
} from 'lucide-react';
import { useNotificationStore } from '../stores/notificationStore';
import type {
  ToastNotificationProps,
  ToastNotification as ToastType,
  Notification,
  NotificationCategory,
} from '../types/notificationsTypes';

// ----------------------------------------------------------------------------
// Icon Mapping
// ----------------------------------------------------------------------------

const categoryIcons: Record<NotificationCategory, React.ElementType> = {
  appointment: Calendar,
  health: Heart,
  document: FileText,
  community: Users,
  legal: Scale,
  marketplace: ShoppingCart,
  system: Settings,
};

// ----------------------------------------------------------------------------
// Single Toast Component
// ----------------------------------------------------------------------------

const Toast: React.FC<{
  toast: ToastType;
  onDismiss: (id: string) => void;
  onAction?: (notification: Notification) => void;
}> = ({ toast, onDismiss, onAction }) => {
  const [isExiting, setIsExiting] = useState(false);
  const { notification } = toast;
  const Icon = categoryIcons[notification.category] || Settings;

  // Handle dismiss with animation
  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      onDismiss(toast.id);
    }, 200);
  };

  // Handle action click
  const handleAction = () => {
    onAction?.(notification);
    handleDismiss();
  };

  // Get category colors
  const getCategoryColors = () => {
    switch (notification.category) {
      case 'appointment':
        return 'bg-sage/15 text-sage-dark';
      case 'health':
        return 'bg-terracotta/15 text-terracotta';
      case 'document':
        return 'bg-navy/10 text-navy';
      case 'community':
        return 'bg-blue-100 text-blue-600';
      case 'legal':
        return 'bg-navy/15 text-navy';
      case 'marketplace':
        return 'bg-terracotta/10 text-terracotta';
      case 'system':
        return 'bg-warm-gray/15 text-warm-gray';
      default:
        return 'bg-cream-dark text-navy';
    }
  };

  // Priority border color
  const getPriorityBorder = () => {
    switch (notification.priority) {
      case 'urgent':
        return 'border-l-4 border-l-red-500';
      case 'high':
        return 'border-l-4 border-l-terracotta';
      default:
        return '';
    }
  };

  return (
    <div
      className={`
        w-full max-w-sm bg-white rounded-xl shadow-lifted border border-warm-gray/10
        transform transition-all duration-200
        ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
        ${getPriorityBorder()}
      `}
      role="alert"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={`p-2 rounded-lg ${getCategoryColors()} flex-shrink-0`}>
            <Icon className="w-4 h-4" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 pt-0.5">
            <p className="text-sm font-medium text-navy line-clamp-1">
              {notification.title}
            </p>
            <p className="text-xs text-warm-gray mt-0.5 line-clamp-2">
              {notification.body}
            </p>

            {/* Action button */}
            {notification.actionUrl && (
              <button
                onClick={handleAction}
                className="
                  mt-2 inline-flex items-center gap-1
                  text-xs font-medium text-sage hover:text-sage-dark
                  transition-colors
                "
              >
                View details
                <ExternalLink className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="p-1 rounded-lg hover:bg-cream text-warm-gray hover:text-navy transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Progress bar (auto-dismiss indicator) */}
      {toast.duration && toast.duration > 0 && (
        <div className="h-1 bg-cream rounded-b-xl overflow-hidden">
          <div
            className="h-full bg-sage/50 animate-shrink-width"
            style={{
              animationDuration: `${toast.duration}ms`,
            }}
          />
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Toast Container Component
// ----------------------------------------------------------------------------

export const ToastContainer: React.FC<{
  onAction?: (notification: Notification) => void;
}> = ({ onAction }) => {
  const { toasts, removeToast } = useNotificationStore();

  // Create portal to render toasts at document root
  return createPortal(
    <div
      className="
        fixed bottom-4 right-4 z-[9999]
        flex flex-col-reverse gap-3
        pointer-events-none
      "
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <Toast
            toast={toast}
            onDismiss={removeToast}
            onAction={onAction}
          />
        </div>
      ))}
    </div>,
    document.body
  );
};

// ----------------------------------------------------------------------------
// Standalone Toast Function (for programmatic use)
// ----------------------------------------------------------------------------

let toastIdCounter = 0;

export function showToast(
  notification: Notification,
  options?: { duration?: number }
) {
  const store = useNotificationStore.getState();
  store.addToast(notification, options?.duration);
}

// ----------------------------------------------------------------------------
// Custom Toast Hook
// ----------------------------------------------------------------------------

export function useToast() {
  const { addToast, removeToast, clearToasts } = useNotificationStore();

  const show = (notification: Notification, duration?: number) => {
    addToast(notification, duration);
  };

  const dismiss = (toastId: string) => {
    removeToast(toastId);
  };

  const dismissAll = () => {
    clearToasts();
  };

  return {
    show,
    dismiss,
    dismissAll,
  };
}

// ----------------------------------------------------------------------------
// Simple Toast Variants (for quick notifications)
// ----------------------------------------------------------------------------

interface SimpleToastOptions {
  title: string;
  message: string;
  category?: NotificationCategory;
  duration?: number;
  actionUrl?: string;
}

export function toast(options: SimpleToastOptions) {
  const notification: Notification = {
    id: `toast-${++toastIdCounter}`,
    userId: '',
    notificationType: 'simple',
    category: options.category || ('system' as NotificationCategory),
    title: options.title,
    body: options.message,
    actionUrl: options.actionUrl,
    priority: 'normal' as const,
    isRead: false,
    emailSent: false,
    pushSent: false,
    smsSent: false,
    createdAt: new Date().toISOString(),
  };

  const store = useNotificationStore.getState();
  store.addToast(notification, options.duration || 5000);
}

toast.success = (title: string, message: string) => {
  toast({ title, message, category: 'system' as NotificationCategory });
};

toast.error = (title: string, message: string) => {
  toast({ title, message, category: 'system' as NotificationCategory, duration: 8000 });
};

toast.info = (title: string, message: string) => {
  toast({ title, message, category: 'system' as NotificationCategory });
};

// ----------------------------------------------------------------------------
// CSS Animation (add to global styles)
// ----------------------------------------------------------------------------

/*
@keyframes shrink-width {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

.animate-shrink-width {
  animation: shrink-width linear forwards;
}
*/

export default ToastContainer;


